import { isImpersonating } from '@/stores/impersonate'
import { Settings } from '../../app/Settings'
import { IS_SSR } from '../../app/constants'
import * as TestPanel from '../../app/testPanel'
import { QueryKeys } from '../api/session/queryKeys'
import { analyticsDebug } from './debug'
import type { AnalyticsProperties, AnalyticsUser, ServerTarget } from './types'

/**
 * Distinguishes an event the user performed in the browser from the same fact observed by
 * the API or reported by the app. Every name the web app sends carries it.
 */
export const PLATFORM_SUFFIX = '_w'

export const withPlatformSuffix = (event: string) => `${event}${PLATFORM_SUFFIX}`

/**
 * Whether this session reports against the development Segment source.
 *
 * The analytics bypass decides it, and nothing else — not the build, not the server
 * selected in the test panel, not whether the panel is open. A build environment is a poor
 * proxy for intent: the same deploy serves a tester who wants their events quarantined and
 * a real user whose events belong in production, and only an explicit switch tells them
 * apart.
 *
 * Having the panel open is not that switch. It is reachable only through the panel — the
 * toggle renders nowhere else — so it adds no reach, only an implicit quarantine that
 * would silently drop a real user's events for as long as someone left the panel on.
 */
export const isDebugActive = () => analyticsDebug.isBypassEnabled()

/**
 * The single gate for every analytics send. Evaluated per call, because the profile it
 * reads only exists after login.
 */
export function isAnalyticsEnabled() {
  // Debug override (test panel): short-circuits every guard below so QA can exercise the
  // pipeline without pointing the app at the production backend, which is what selecting
  // the production server would otherwise also do. Reports against the development
  // Segment source — see `isDebugActive`.
  if (analyticsDebug.isBypassEnabled()) return true

  const profile = QueryKeys.me.getData()
  const sanitizedEmail = profile?.email?.toLowerCase().trim() || ''

  // Exclude Codeleap team members and test users from analytics. Real addresses end in
  // `codeleap.co.uk`, so this has to be a substring test, not a suffix one.
  const isCodeleapTeam = sanitizedEmail.includes('codeleap')
  const isTestUser = sanitizedEmail.includes('test')

  // Exclude impersonating users, and only enable analytics in production.
  // `Settings.EnableAnalytics` already carries the environment decision (including the
  // deliberate GATSBY_DEV_ENV + TESTING_ANALYTICS override), so no NODE_ENV term belongs
  // here — one used to, and since it contradicted EnableAnalytics the whole conjunction
  // was unsatisfiable and the web app sent nothing at all.
  const impersonating = isImpersonating()
  const isProduction = TestPanel.isProduction()

  return [!impersonating, isProduction, !isCodeleapTeam, !isTestUser, !IS_SSR, Settings.EnableAnalytics].every(Boolean)
}

/**
 * Flattens a profile into the trait set Segment and its destinations key on. Kept pure and
 * separate from the client so trait changes can be read, tested and compared against
 * mobile's `getUserTraits` without going through an SDK.
 */
export function getUserTraits(
  user: AnalyticsUser,
  isLoggedIn: boolean,
  server: ServerTarget,
  anonymousId: string | null | undefined
): AnalyticsProperties {
  return {
    id: user?.id,
    email: user?.email,
    platform: 'web',
    referral_code: user?.referral_code,
    anonymousId,
    address: { country: user?.country },
    company: {
      name: user?.organization?.name,
      id: user?.organization?.id,
      employee_count: user?.organization?.max_employees,
      industry: user?.organization?.category?.label
    },
    badges: user?.badges,
    gender: user?.gender,
    username: user?.display_name,
    name: user?.name,
    advertising_source: user?.inbound_source,
    climate_priority: user?.climate_priority,
    satisfaction_at_work: user?.satisfaction_at_work,
    date_of_birth: user?.birthday,
    function: user?.department,
    seniority: user?.seniority,
    logged_in: isLoggedIn,
    server,
    climate_empowerment_score: user?.organisationRank?.label,
    climate_empowerment_number: user?.score?.overall,
    head_score: user?.score?.categories[1]?.total,
    heart_score: user?.score?.categories[2]?.total,
    hands_Score: user?.score?.categories[3]?.total, // maintain hands_Score instead of hands_score for compatibility
    initial_utm: user?.utm
  }
}
